/*
 * T2 FINDINGS REVIEW — OWNER ONLY. Aggregate, k-thresholded patterns (never member data). Approval marks a
 * finding for a human-made change; nothing is applied, trained or tuned automatically.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ClipboardCheck } from "lucide-react";
import { Panel, Note, Chip } from "./ConsolePrimitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/founder/ci-t2-findings`;

const Row = ({ f, busy, onReview }) => (
  <li data-testid={`fc-t2-finding-${f.finding_id}`}
    style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.08)", flexWrap: "wrap" }}>
    <span>
      <b>{f.dimension.replace("_", " ")}: {f.value}</b> · {f.language.toUpperCase()} · {f.contributors_bucket} members · {f.share_band}
    </span>
    {f.state === "observed" ? (
      <span style={{ display: "flex", gap: 8 }}>
        <button type="button" className="fc-btn" disabled={busy} data-testid={`fc-t2-approve-${f.finding_id}`}
          onClick={() => onReview(f.finding_id, "approved_for_human_change")}>Approve for human change</button>
        <button type="button" className="fc-btn fc-btn--ghost" disabled={busy} data-testid={`fc-t2-reject-${f.finding_id}`}
          onClick={() => onReview(f.finding_id, "rejected")}>Reject</button>
      </span>
    ) : (
      <Chip tone={f.state === "invalidated" ? "warn" : "quiet"} testId={`fc-t2-state-${f.finding_id}`}>
        {f.state.replaceAll("_", " ")}{f.retired_reason ? ` (${f.retired_reason.replaceAll("_", " ")})` : ""}
      </Chip>
    )}
  </li>
);

export const T2FindingsReview = () => {
  const [data, setData] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      setData((await axios.get(API)).data);
    } catch (e) {
      setNote(e?.response?.status === 503 ? "T2 review is not active in this environment." : "T2 findings could not be read just now.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const review = async (id, decision) => {
    setBusy(id);
    try { await axios.post(`${API}/${id}/review`, { decision }); }
    catch (e) { setNote(e?.response?.data?.detail || "That review could not be saved."); }
    finally { setBusy(null); load(); }
  };

  return (
    <Panel title="T2 findings awaiting review" icon={ClipboardCheck} testId="fc-t2-panel">
      {note && <Note testId="fc-t2-note">{note}</Note>}
      {data && !data.findings.length && <Note testId="fc-t2-empty">No T2 findings yet.</Note>}
      {data?.findings?.length > 0 && (
        <ul data-testid="fc-t2-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {data.findings.map((f) => <Row key={f.finding_id} f={f} busy={busy === f.finding_id} onReview={review} />)}
        </ul>
      )}
      <Note>Approval never changes Arvexis by itself: a person makes any change, and no model is trained or tuned.</Note>
    </Panel>
  );
};
